/* eslint eslint-comments/no-use: off */
/* eslint-disable relay/unused-fields */
import {setTitle as setDocumentTitle} from '@github-ui/document-metadata'
import type React from 'react'
import {useEffect, useRef, useState} from 'react'
import {graphql, type PreloadedQuery, useFragment} from 'react-relay'
import {usePreloadedQuery} from 'react-relay/hooks'

import type {Header$data, Header$key} from './__generated__/Header.graphql'
import type {HeaderIssueTitle$key} from './__generated__/HeaderIssueTitle.graphql'
import type {HeaderQuery} from './__generated__/HeaderQuery.graphql'
import {HeaderEditor} from './HeaderEditor'
import {HeaderViewer, type HeaderViewerProps} from './HeaderViewer'
import {Box, Flash, IconButton} from '@primer/react'
import {HeaderMetadata, type HeaderMetadataProps} from './HeaderMetadata'
import {ObservableBox, useStickyHeader} from '@github-ui/use-sticky-header'
import {ContentWrapper} from '../ContentWrapper'
import {XIcon} from '@primer/octicons-react'
import type {OptionConfig} from '../OptionConfig'
import type {HeaderMenuProps} from './HeaderMenu'
import type {StickyHeaderTitleProps} from './StickyHeaderTitle'

export const HeaderGraphqlQuery = graphql`
  query HeaderQuery($owner: String!, $repo: String!, $number: Int!) {
    repository(owner: $owner, name: $repo) {
      issue(number: $number) {
        ...Header
        issueType {
          ...IssueTypesDialogIssueType
        }
      }
      issueTypes(first: 10) {
        ...IssueTypesDialogIssueTypes
      }
    }
  }
`

const headerIssueTitleFragment = graphql`
  fragment HeaderIssueTitle on Issue {
    title
    titleHTML
    number
    repository {
      nameWithOwner
    }
  }
`

type HeaderBaseProps = {
  optionConfig: OptionConfig
  issueId: string
  metadataPaneTrigger: JSX.Element
}

export type HeaderFetchedProps = HeaderBaseProps & {
  issuesQueryRef: PreloadedQuery<HeaderQuery>
}

type HeaderProps = HeaderBaseProps & {
  issue: Header$key | null
}

type HeaderInternalProps = Omit<HeaderProps, 'issue'> & {
  data: Header$data
}

export function HeaderFetched({issuesQueryRef, ...rest}: HeaderFetchedProps) {
  const preloadedData = usePreloadedQuery<HeaderQuery>(HeaderGraphqlQuery, issuesQueryRef)
  return (
    <Header
      issue={preloadedData.repository && preloadedData.repository.issue ? preloadedData.repository.issue : null}
      {...rest}
    />
  )
}

export function Header({issue, ...rest}: HeaderProps) {
  const data = useFragment(
    graphql`
      fragment Header on Issue {
        id
        author {
          login
        }
        isTransferInProgress
        createdAt
        repository {
          id
          name
          isPrivate
          owner {
            __typename
            avatarUrl(size: 64)
            login
          }
        }
        url
        ...HeaderState
        ...HeaderIssueTitle
        ...StickyHeaderTitle
        ...HeaderMenu
        ...HeaderViewer
        ...RepositoryPill
        tasklistBlocksCompletion {
          ...TaskListStatus
        }
        issueType {
          id
          name
        }
      }
    `,
    issue,
  )
  return data ? <HeaderInternal data={data} {...rest} /> : null
}

function HeaderInternal({optionConfig, data, issueId, metadataPaneTrigger}: HeaderInternalProps) {
  const {
    title: issueTitle,
    number,
    repository: {nameWithOwner},
  } = useFragment(headerIssueTitleFragment, data as HeaderIssueTitle$key)

  const [title, setTitle] = useState<string>(issueTitle)
  const titleRef = useRef<HTMLInputElement>(null)
  const [isIssueTitleEditActive, setIsIssueTitleEditActive] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const shouldUpdateDocumentTitle = optionConfig.selectedProjectId === undefined
  useEffect(() => {
    if (issueTitle && shouldUpdateDocumentTitle) {
      setDocumentTitle(`${title} · Issue #${number} · ${nameWithOwner}`)
    }
  }, [shouldUpdateDocumentTitle, issueTitle, nameWithOwner, number, title])

  useEffect(() => {
    if (isIssueTitleEditActive) return
    setTitle(issueTitle)
  }, [isIssueTitleEditActive, issueTitle, setTitle])

  const onTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const oldValue = title
    const newValue = e.target.value
    if (newValue !== null && newValue !== oldValue) {
      setTitle(newValue)
    }
  }

  // Focus on title when user clicks on edit
  useEffect(() => {
    if (isIssueTitleEditActive && titleRef.current) {
      titleRef.current.focus()
    }
  }, [isIssueTitleEditActive])

  const {isSticky, stickyStyles, observe, unobserve} = useStickyHeader()
  const [showTransferWarning, setShowTransferWarning] = useState(data.isTransferInProgress)

  if (!data) {
    return null
  }

  const headerTitleProps: StickyHeaderTitleProps = {
    headerTitleData: data,
  }

  const headerMenuProps: HeaderMenuProps = {
    headerMenuData: data,
    optionConfig,
    metadataPaneTrigger,
    isSticky,
  }

  const headerViewerProps: HeaderViewerProps = {
    headerViewerData: data,
    setIsIssueTitleEditActive,
    ...headerTitleProps,
    ...headerMenuProps,
  }

  const headerMetadataProps: HeaderMetadataProps = {
    headerData: data,
    typeData: data.issueType,
    stickyStyles,
    taskListStatusData: data.tasklistBlocksCompletion ?? undefined,
    ...headerTitleProps,
    ...headerMenuProps,
  }

  return (
    <>
      <ContentWrapper sx={optionConfig.innerSx}>
        {showTransferWarning && (
          <Flash
            aria-live="polite"
            sx={{mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}
          >
            Issue transfer in progress
            <IconButton
              variant="invisible"
              aria-label="Dismiss issue is in transfer notification"
              icon={XIcon}
              onClick={() => setShowTransferWarning(false)}
            />
          </Flash>
        )}
        {isIssueTitleEditActive || isSubmitting ? (
          <HeaderEditor
            issueId={issueId}
            title={title}
            titleRef={titleRef}
            onTitleChange={onTitleChange}
            onIssueUpdate={optionConfig.onIssueUpdate}
            cancelIssueTitleEdit={() => {
              setIsSubmitting(false)
              setIsIssueTitleEditActive(false)
            }}
            isDirty={issueTitle !== title}
            isSubmitting={isSubmitting}
            setIsSubmitting={setIsSubmitting}
          />
        ) : (
          <HeaderViewer {...headerViewerProps} />
        )}
      </ContentWrapper>
      <ObservableBox
        sx={{top: '-1px', height: '1px', visibility: 'hidden'}}
        onObserve={observe}
        onUnobserve={unobserve}
      />
      <HeaderMetadata {...headerMetadataProps} />
      <ContentWrapper sx={{mb: 3, ...optionConfig.innerSx}}>
        <Box
          sx={{
            width: '100%',
            marginBottom: 3,
          }}
        />
      </ContentWrapper>
    </>
  )
}

try{ HeaderFetched.displayName ||= 'HeaderFetched' } catch {}
try{ Header.displayName ||= 'Header' } catch {}
try{ HeaderInternal.displayName ||= 'HeaderInternal' } catch {}